import { useEffect, useReducer, useRef } from 'react';
import { NetworkStatus } from '@apollo/client';

import {
  useStreamRepositoryInjector,
  useBillingRepositoryInjector,
  usePointRepositoryInjector,
} from 'application/providers/DIContainerProvider';
import { useAccountContext } from 'application/providers/AccountProvider';
import { DEFAULT_PAGINATION_INFO, DEFAULT_SHORT_PAGINATION, TARIFFS } from 'application/pages/constants';
import CompanyUtils from 'application/utils/CompanyUtils';
import { ItemShortType } from 'application/components/SelectableList/types';

import useUserFeaturesOnboardingPass from 'domain/media/useUserFeaturesOnboardingPass';
import useUserFeaturesOnboarding from 'domain/media/useUserFeaturesOnboarding';
import {
  AcceptanceAdvanceStatus,
  PaymentKind,
  PointQueryFilter,
  PointQueryPagination,
  PointQuerySort,
  UserFeatures,
} from 'domain/api/graphql/generated';
import useStreamSetToPoints from 'domain/media/streams/useStreamSetToPoints';
import useAcceptanceAdvanced from 'domain/billing/useAcceptanceAdvanced';
import usePointList from 'domain/point/usePointList';

import { DEFAULT_FILTERS, DEFAULT_STREAM_SET_POINTS_INPUT_STATE } from './consts';
import { streamSetPointsInputReducer } from './reducers';
import { StreamSetPointsInputActionTypes, UseStreamBindModalViewModel } from './types';

const useStreamBindModalViewModel = (): UseStreamBindModalViewModel => {
  const { currentCompany, companyIds } = useAccountContext();

  const {
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    copyTemplateStream,
    getSavedStreams,
    getStream,
    readStreamWithThematic,
    clearStreamWithThematic,
  } = useStreamRepositoryInjector();

  const { getBillingInfo, watchBillingInfo } = useBillingRepositoryInjector();

  const { selectedPaymentKind } = watchBillingInfo(currentCompany!.id);

  const { clearCurrentPoint } = usePointRepositoryInjector();

  const { request: onboardingPassedRequest, result: onboardingPassedResult } = useUserFeaturesOnboardingPass();
  const {
    items: userOnboardingFeatures,
    refetch: refetchFeaturesOnboarding,
    loading: userOnboardingFeaturesLoading,
  } = useUserFeaturesOnboarding({
    features: [UserFeatures.WarningStreamFirstStart],
  });

  const { loading: acceptanceAdvancedLoading, acceptanceAdvanced } = useAcceptanceAdvanced(currentCompany!.id);

  const [streamSetToPointsRequest, streamSetToPointsResult] = useStreamSetToPoints();
  const [requestPointList, pointListResult] = usePointList();

  const filtersRef = useRef<PointQueryFilter>({
    ...DEFAULT_FILTERS,
    companyIds,
  });

  const paginationRef = useRef<PointQueryPagination>(DEFAULT_SHORT_PAGINATION);

  const sortRef = useRef<PointQuerySort>(PointQuerySort.IdDesc);

  const [state, dispatch] = useReducer(streamSetPointsInputReducer, DEFAULT_STREAM_SET_POINTS_INPUT_STATE);

  const setPointInput = (needAdd: boolean, point: ItemShortType) => {
    dispatch({ type: StreamSetPointsInputActionTypes.SET_POINT, needAdd, point });
  };

  const requestPoints = async (
    filters: PointQueryFilter = DEFAULT_FILTERS,
    pagination: PointQueryPagination = DEFAULT_SHORT_PAGINATION
  ) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      companyIds,
    };

    paginationRef.current = {
      ...paginationRef.current,
      ...pagination,
    };

    await requestPointList(filtersRef.current, paginationRef.current, sortRef.current);
  };

  const loadMorePoints = async () => {
    const page = (pointListResult.data.paginationInfo?.page || 0) + 1;

    if (page <= paginationRef.current.page) return;

    paginationRef.current.page = page;

    await pointListResult.loadMore(filtersRef.current, paginationRef.current, sortRef.current);
  };

  const onFilterExecute = async (filters: PointQueryFilter) => {
    const preparedFilters: PointQueryFilter = {
      companyIds,
    };

    const { query } = filters;

    if (query !== undefined) {
      preparedFilters.query = query;
      filtersRef.current.query = query;
    }

    if (!preparedFilters.query && filtersRef.current.query) {
      preparedFilters.query = filtersRef.current.query;
    }

    await requestPointList(preparedFilters, paginationRef.current, sortRef.current);
  };

  const streamSetToPoints = async (streamId: string) => {
    const pointIds = state.filter && state.filter.pointIds ? [...state.filter.pointIds.keys()] : [];
    const isOk = await streamSetToPointsRequest({ companyId: currentCompany!.id, streamId, pointIds });

    if (isOk) {
      dispatch({ type: StreamSetPointsInputActionTypes.RESET_STATE });
    }
  };

  const pointsLoading =
    pointListResult.networkStatus === NetworkStatus.loading ||
    pointListResult.networkStatus === NetworkStatus.setVariables;

  const pointsCalled = pointListResult.called;
  const points = pointListResult.data.points || [];

  const isLoading =
    isChangeStreamLoading ||
    isCopyStreamTemplateLoading ||
    onboardingPassedResult.loading ||
    userOnboardingFeaturesLoading ||
    pointsLoading;

  const isPopularTariff = CompanyUtils.compareTariff(currentCompany?.pricePlan?.alias ?? '', TARIFFS.POPULAR);
  const isCustomInfoShouldShow = !userOnboardingFeatures[0]?.isOnboarded && isPopularTariff;
  const isBillingEnabled = currentCompany?.isBillingEnabled;

  const isReady = pointsCalled && (!pointsLoading || points.length !== 0);
  const isFilterQueryEmpty = !filtersRef.current.query;
  const isEmpty = isReady && points.length === 0 && isFilterQueryEmpty;
  const isEmptyFiltered = isReady && points.length === 0 && !isFilterQueryEmpty;

  const isAcceptanceAdvancedPending =
    selectedPaymentKind === PaymentKind.Sbbol &&
    (acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Pending || acceptanceAdvancedLoading);

  useEffect(() => {
    if (isBillingEnabled) {
      getBillingInfo(currentCompany?.id!);
    }

    requestPoints(filtersRef.current, paginationRef.current);
  }, []);

  return {
    streamSetPointsState: state,
    isReady,
    isEmpty,
    isEmptyFiltered,
    points,
    pointsLoading,
    paginationInfo: pointListResult.data.paginationInfo || DEFAULT_PAGINATION_INFO,
    allPointsCount: pointListResult.data.paginationInfo?.totalCount || points.length,
    isCustomInfoShouldShow,
    isLoading,
    userOnboardingFeaturesLoading,
    isAcceptanceAdvancedPending,
    onFilterExecute,
    loadMorePoints,
    streamSetToPoints,
    streamSetToPointsLoading: streamSetToPointsResult.loading,
    copyTemplateStream,
    getSavedStreams,
    getStream,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    clearCurrentPoint,
    readStreamWithThematic,
    clearStreamWithThematic,
    setPointInput,
  };
};

export default useStreamBindModalViewModel;
